import React, { Component, useState, useEffect } from 'react';
import Category from '../components/Category';
import * as pdfjsLib from 'pdfjs-dist';
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import WordHelper from "../../src/components/utils/WordHelper";
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import Spinner from "../components/Spinner";
import DocumentDeliveredCard from "../components/DocumentDelieveredCard";
import {
    GenerateTemplatePreview,
    GetTemplate,
    GetTemplateFields,
    ValidateTemplateFields
} from "../ApiClients/TemplateApiClient";
import {GetDocumentById, SaveDocument} from "../ApiClients/DocumentApiClient";
import { EnsureLoggedIn, ExchangeTokenKeyToAccessToken, IsLoggedIn } from '../services/AuthenticationService';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

export function TemplateEditor() {
    const { id, documentId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [title, setTitle] = useState("");
    const [templateId, setTemplateId] = useState(null);
    const [list, setList] = useState({});
    const [timeoutId, setTimeoutId] = useState(0);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [canvas, setCanvas] = useState(null);
    const [formatDropdownIsOpen, setFormatDropdownIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [documentBytes, setDocumentBytes] = useState(new Uint8Array);
    const [isWord, setIsWord] = useState(window.location.toString().toLowerCase().includes('word'));
    const [changesSaved, setChangesSaved] = useState(false);
    
    const [documentNotFound, setDocumentNotFound] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {


        const handleParameters = async () => {
            

            if (documentId !== undefined) {
                let exchangeResult = await ExchangeTokenKeyToAccessToken(documentId);

                if (!exchangeResult) {
                    navigate("/AccessDenied");
                    return;
                }
            }


            if (!EnsureLoggedIn(navigate)) {
                return;
            }


            if (documentId === undefined) {
            
                setTemplateId(id);
                let response = await GetTemplateFields(id);
                await onLoad(response.data, id);   
    
            } else {
                
                try {
                    let response = await GetDocumentById(documentId);
                    let documentData = response.data;

                    
                    setTemplateId(documentData.templateId);
                    await onLoad(documentData.templateRequirements, documentData.templateId);
                } catch(e) {
                    console.log(e.message);

                    if (e.response.status === 404) {
                        setDocumentNotFound(true)
                    }
                }
                

            }


        }


        handleParameters()
        .catch(function(error) {
            console.log(error.message);
        });

    }, []);

    async function onLoad(output, currentTemplateId) {
        setList(output);
        
        GetTemplate(currentTemplateId).then(function(response) {
            setTitle(response.data.name);
        })
        .catch(function(error) {
            console.log(error.message);
        })
        
        console.log(currentTemplateId);
        console.log(output);
        
        await generatePreview(currentTemplateId, output);
    }
    
    async function generatePreview(id, fields) {
        
        try {
            //Finds all textareas, and change \n to \v so it makes a soft break insted of a hard break
            const elementsWithMultiLineTrue = fields.fieldGroups.flatMap(item => item.fields.filter(field => field.multiLine === true));
            elementsWithMultiLineTrue.forEach(element => {
                element.value = element.value.replace(/\n/g, '\v');
            });
            let response = await GenerateTemplatePreview(id, fields.fieldGroups);
            let data = response.data;
            
            SetPreview(data);
            setDocumentBytes(new Uint8Array(atob(data.bytes).split('').map(char => char.charCodeAt(0))));
        } catch (e) {
            console.log(e.message);
        }
        finally{
            setIsLoading(false)
        }
    }

    function validateList() {


        console.log("Running validation");
        clearTimeout(timeoutId);
        setIsLoading(true)
        
        setTimeoutId(setTimeout(async () => {
            
            try {
                
                let response = await ValidateTemplateFields(templateId, list.fieldGroups);
                let data = response.data;
                
                setList(data);
                console.log(data);
                
                await generatePreview(templateId, data);
   
            } catch (e) {
                console.log(e.message);
            }
            
        }, 500));
    }

    function SetPreview(data) {
        const bytes = atob(data.bytes)
            .split('')
            .map(char => char.charCodeAt(0));
        const uint8Array = new Uint8Array(bytes);

        pdfjsLib.getDocument(uint8Array).promise.then((doc) => {
            console.log('PDF loaded')
            setPdfDoc(doc);
        }).catch((error) => {
            console.error('Error loading PDF document:', error);
        });
    }

    useEffect(() => {
        // Get the canvas element and render the first page
        const canvasEl = document.getElementById('pdf-canvas');
        const canvasFrameEl = document.getElementById('canvas-frame');
        const paginationButtons = document.getElementById('pagination-buttons');
        setCanvas(canvasEl);

        if (pdfDoc) {
            // If on last page of preview and the total number of pages are reduced, then set current page to last page.
            if (pageNum > pdfDoc.numPages) {
                setPageNum(pdfDoc.numPages)
            }

            pdfDoc.getPage(pageNum).then((page) => {
                //const viewport = page.getViewport({ scale: 1.5 });

                const desiredCanvasHeight = window.innerHeight - 150; // Set your desired canvas height here
                const viewport = page.getViewport({ scale: 1.1 });
                const scale = desiredCanvasHeight / viewport.height; // Calculate the scale value

                const scaledViewport = page.getViewport({ scale });

                const context = canvasEl.getContext('2d');
                canvasEl.className = " border border-gray-500";
                canvasEl.width = scaledViewport.width;
                canvasEl.height = scaledViewport.height;

                page.render({ canvasContext: context, viewport: scaledViewport });

                // Show/hide pagination on mouse hover
                hoverEvent(canvasFrameEl, paginationButtons);

                // Disable right-click on the canvas
                canvasEl.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                });
            }).catch((error) => {
                console.error('Error rendering page:', error);
            });
        }
    }, [pdfDoc, pageNum]);

    function hoverEvent(element, element2) {
        element.addEventListener('mouseover', () => {
            element2.hidden = false;
        });
        element.addEventListener('mouseout', () => {
            element2.hidden = true;
        });
    }

    const handlePrevPage = () => {
        if (pageNum > 1) {
            setPageNum(pageNum - 1);
        }
    };

    const handleNextPage = () => {
        if (pdfDoc && pageNum < pdfDoc.numPages) {
            setPageNum(pageNum + 1);
        }
    };

    const tabs = [
        {
            name: 'Opdater visning', onClick: async () => {
                setIsLoading(true);
                await generatePreview(templateId, list);
            }
        },
        { name: 'Gem som', onClick: () => saveAsClicked() },
    ]

    function classNames(...classes) {
        return classes.filter(Boolean).join(' ')
    }

    //Not used anymore below?
    async function saveAsClicked() {
        if (!isWord)
            setFormatDropdownIsOpen(!formatDropdownIsOpen);
        else
            await createTemplate();
    }

    async function createPdfFile() {
        await saveAsClicked();
        
        if (documentId != null) {
            await deliverToAdapter(documentBytes, "PDF", false);
        }
        else {
            const blob = new Blob([documentBytes], { type: 'application/pdf' });
            const anchor = document.createElement('a');

            if (title != "") {
                anchor.download = title;
            } else {
                anchor.download = "pdfpreview.pdf";
            }

            anchor.style.display = "none";
            anchor.href = URL.createObjectURL(blob);

            document.body.appendChild(anchor);
            anchor.click();

            document.body.removeChild(anchor);
            URL.revokeObjectURL(anchor.href);
        }
        

    }

    function arrayBufferToBase64( buffer ) {
        var binary = '';
        var bytes = new Uint8Array( buffer );
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode( bytes[ i ] );
        }
        return window.btoa( binary );
    }

    async function deliverToAdapter(byteArray, fileType, isDraft) {
        
        setIsLoading(true);
        let base64 = arrayBufferToBase64(byteArray);
        
        try {
            await SaveDocument(documentId, base64, fileType, isDraft);
            setIsLoading(false);
            setChangesSaved(true);
        } catch (e) {
            console.log(e.message);
            alert("Der opstod en uventet fejl. Dine ændringer blev ikke gemt.");
        }
    }

    async function createWordFile() {
        
        try {
            
            let validationResponse = await ValidateTemplateFields(templateId, list.fieldGroups);
            let validationData = validationResponse.data;
            
            let previewResponse = await GenerateTemplatePreview(templateId, validationData.fieldGroups, "Default");
            let previewData = previewResponse.data;
            
            await saveAsClicked();

            const documentBytesArray = base64ToBytes(previewData.bytes);


            if (documentId != null) {
                await deliverToAdapter(documentBytesArray, "Default", false);
            }
            else {
                const blob = new Blob([documentBytesArray], { type: 'application/pdf' });
                const anchor = document.createElement('a');

                if (title != null) {
                    anchor.download = `${title}.docx`;
                } else {
                    anchor.download = previewData.fileName;
                }

                anchor.style.display = "none";
                anchor.href = URL.createObjectURL(blob);

                document.body.appendChild(anchor);
                anchor.click();

                document.body.removeChild(anchor);
                URL.revokeObjectURL(anchor.href);
            }
            
        } catch (e) {
            console.log(e.message);
        }
        
    }

    function base64ToBytes(base64) {
        var binaryString = atob(base64);
        var bytes = new Uint8Array(binaryString.length);

        for (var i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes;
    }

    async function createTemplate() {
        
        try {
            let validateResponse = await ValidateTemplateFields(templateId, list.fieldGroups);
            let validateData = validateResponse.data;
            
            let previewResponse = await GenerateTemplatePreview(templateId, validateData, "Default");
            let previewData = previewResponse.data;
            
            WordHelper.CreateNewDocument(previewData.bytes);
            
        } catch (e) {
            console.log(e.message);
        }
    }

    async function saveDraft() {
        await saveAsClicked();
        
        if (documentId != null) {

            try {
                let validateResponse = await ValidateTemplateFields(templateId, list.fieldGroups);
                
                let json = JSON.stringify(validateResponse.data);
                let bytes = new TextEncoder().encode(json);

                await deliverToAdapter(bytes, "Default", true);

            } catch (e) {
                console.log(e.message);
            }
            
          
            
        } else {
            const jsonListStr = JSON.stringify(list);
            const jsonListStr2 = JSON.stringify(jsonListStr);
            console.log(jsonListStr2);

            copyToClipboard(jsonListStr2);
        }
        

    }

    function copyToClipboard(text) {
        // Create a temporary textarea element
        const textarea = document.createElement('textarea');
        textarea.value = text;

        // Make it hidden
        textarea.style.position = 'fixed';
        textarea.style.opacity = 0;

        // Append the textarea to the DOM
        document.body.appendChild(textarea);

        // Select the text inside the textarea
        textarea.select();

        try {
            // Copy the selected text to the clipboard
            document.execCommand('copy');
            console.log('Text copied to clipboard');
        } catch (error) {
            console.error('Unable to copy text to clipboard:', error);
        }

        // Remove the textarea from the DOM
        document.body.removeChild(textarea);
    }

    function decodeBase64(base64Draft) {
        /*For testing*/
        //var tempUrl = "https://localhost:44410/templateeditor/0454db31-0bcf-4a3c-918e-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";
        //var urlSplitArray = tempUrl.split('/');
        //base64Draft = urlSplitArray[urlSplitArray.length - 1];

        var decodedString = atob(base64Draft);
        var jsonString = JSON.parse(decodedString);

        return jsonString;
    }

    if (documentNotFound) {
        return (
            <div className="bg-gray-200 w-full px-16 md:px-0 h-screen flex items-center justify-center">
                <div className="bg-white border border-gray-200 flex flex-col items-center justify-center px-4 md:px-8 lg:px-24 py-8 rounded-lg shadow-2xl">
                    <p className="text-2xl md:text-3xl lg:text-5xl font-bold tracking-wider text-gray-500 mt-4">Dokument findes ikke.</p>
                    <p className="text-gray-500 mt-4 pb-4 border-b-2 text-center">Det ønskede dokument kunne ikke findes. Dette kan skyldes at vi kun lagre dokumenter midlertidigt. Forsøg igen.</p>

                    
                    <a href="#" className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-gray-100 px-4 py-2 mt-6 rounded transition duration-150" title="Return Home">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clip-rule="evenodd"></path>
                        </svg>
                        <span>Gå til start</span>
                    </a>
                </div>
            </div>
        );
    } else {
        return (

            <div className="flex h-screen">
                <div className="inset-y-0 z-50 flex w-2/12 min-w-px353 flex-col" style={changesSaved ? {opacity: 0.2, pointerEvents: "none"} : {}}>
                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className="flex grow flex-col gap-y-6 overflow-y-auto border-r border-gray-200 bg-gray-50 pb-4" style={(isLoading) ? {opacity: "0.2"} : {}}>
                        <div className="flex mt-14 border-solid border-b-2 border-gray-200 pb-6 mx-4">
                            <div className="grow"></div>
                            <h1 className="grow-0 font-extrabold text-3xl font-normal px-auto">{title}</h1>
                            <div className="grow"></div>
                        </div>
                        {list.fieldGroups != null ? list.fieldGroups.map((item) => (
                            <Category key={item.id} title={item.name} data={item.fields} doValidation={validateList} />
                        )) : null}
                    </div>
                    {isLoading &&  
                        <div className="absolute inset-y-0 z-50 flex w-2/12 min-w-px353 flex-col" style={{ backgroundColor: 'transparent', zIndex: 60 }}>
                        </div>
                    }
                </div>
                <div className="flex-1 overflow-y-auto">
                    <main className="">
                        <div className="static px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center h-screen">
                            <div className="flex-0 mb-4 mt-5" style={{zIndex: "900"}}>
                                <nav className="isolate flex divide-x divide-gray-200 rounded-lg shadow cursor-pointer" aria-label="Tabs" style={changesSaved ? {opacity: "0.2", pointerEvents: "none"} : {}}>
                                    {tabs.map((tab, tabIdx) => (
                                        <span
                                            key={tab.name}
                                            className={classNames(
                                                tabIdx === 0 ? 'rounded-l-lg' : '',
                                                tabIdx === tabs.length - 1 ? 'rounded-r-lg' : '',
                                                'group relative -ml-px inline-flex items-center px-4 py-3 text-sm font-medium text-gray-500'
                                            )}
                                            onMouseEnter={() => {
                                                const borderElement = document.getElementById(`tab-border-${tabIdx}`);
                                                borderElement.classList.add('bg-dania-500');
                                            }}
                                            onMouseLeave={() => {
                                                const borderElement = document.getElementById(`tab-border-${tabIdx}`);
                                                borderElement.classList.remove('bg-dania-500');
                                            }}
                                            onClick={tab.onClick}
                                        >
                                        <span>{tab.name}</span>
                                        <span
                                            id={`tab-border-${tabIdx}`}
                                            aria-hidden="true"
                                            className={classNames(
                                                tabIdx === 0 ? 'ml-1 ' : '',
                                                tabIdx === tabs.length - 1 ? 'mr-1 ' : '',
                                                'absolute inset-x-0 bottom-0 h-0.5 transition-all duration-200'
                                            )}
                                        />
                                    </span>
                                    ))}
                                    {formatDropdownIsOpen && (
                                        <div className="z-50 absolute mt-14 ml-36 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                                            <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                                <a onClick={() => createPdfFile()} className="block px-4 py-2 text-sm text-grey-700 hover:bg-gray-100" role="menuitem">PDF</a>
                                                <a onClick={() => saveDraft()} className="block px-4 py-2 text-sm text-grey-700 hover:bg-gray-100" role="menuitem">Kladde</a>
                                                <a onClick={() => createWordFile()} className="block px-4 py-2 text-sm text-grey-700 hover:bg-blue-100" role="menuitem">Word</a>
                                            </div>
                                        </div>
                                    )}
                                </nav>
                            </div>



                            {pdfDoc ? (



                                <div id="canvas-frame" className="flex-1" style={{position: "relative"}}>

                                    {isLoading && (
                                        <Spinner></Spinner>
                                    )}

                                    {changesSaved && (
                                        <DocumentDeliveredCard></DocumentDeliveredCard>
                                    )}

                                    <canvas id="pdf-canvas" style={(isLoading || changesSaved) ? {opacity: "0.2"} : {}}></canvas>

                                    <div hidden id="pagination-buttons" className="flex-col" style={(isLoading || changesSaved) ? {opacity: "0.2"} : {}}>
                                        <div className="flex relative inset-x-0 bottom-24 items-center justify-center">
                                            <button
                                                className="bg-white hover:bg-gray-300 text-black font-bold py-2 px-4 rounded-l border flex items-center justify-center"
                                                style={{ width: '50px' }}
                                                onClick={() => handlePrevPage()}
                                            >
                                                <ChevronLeftIcon
                                                    className="h-4 w-4 shrink-0"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                            <button
                                                className="bg-white hover:bg-gray-300 text-black font-bold py-2 px-4 rounded-l border flex items-center justify-center"
                                                style={{ width: '50px' }}
                                                onClick={() => handleNextPage()}
                                            >
                                                <ChevronRightIcon
                                                    className="h-4 w-4 shrink-0"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                        <div className="flex relative inset-x-0 bottom-20 items-center justify-center">
                                            <div className="bg-white px-2 py-1 border">
                                                {pageNum + " - " + pdfDoc.numPages}
                                            </div>

                                        </div>
                                    </div>
                                </div>

                            ) : (
                                <div className="flex-1" style={{position: "relative"}}>
                                    <Spinner></Spinner>

                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
    

}
